import { notifications } from "@mantine/notifications"
import {
  UseMutationOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import {
  certificateTypeCreateValidationType,
  certificateTypeUpdateValidationType,
  getCertifcateValidationType,
} from "@tfg/types/schemas/admin"

import { httpClient } from "~/backend/axios"
import { queryClient } from "~/backend/queryClient"

export const useCertificateTypeQuery = ({
  fetchDataOptions,
  searchCertificateType,
  filters,
}: getCertifcateValidationType) => {
  return useQuery(
    ["certificate-types", fetchDataOptions, searchCertificateType, filters],
    async () => {
      const res =
        await httpClient.get<AdminResponses.CertificateTypesWithTotal>(
          "/admin/certificate-types",
          {
            params: {
              fetchDataOptions,
              searchCertificateType,
              filters: JSON.stringify(filters),
            },
          }
        )
      return res.data
    }
  )
}

export const useUpdateCertificateTypeMutation = (
  options?: UseMutationOptions<
    certificateTypeUpdateValidationType,
    unknown,
    certificateTypeUpdateValidationType
  >
) =>
  useMutation<
    certificateTypeUpdateValidationType,
    unknown,
    certificateTypeUpdateValidationType
  >({
    mutationFn: async (certificate: certificateTypeUpdateValidationType) => {
      const res = await httpClient.patch<certificateTypeUpdateValidationType>(
        `/admin/certificate-types`,
        certificate
      )
      return res.data
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
      queryClient.invalidateQueries(["certificate-types"])
      queryClient.invalidateQueries(["certificateTypesList"])
      notifications.show({
        title: "Added",
        message: "Successfully updated Certificate Type",
        color: "green",
      })
    },
  })

export const useCreateCertificateTypeMutation = (
  options?: UseMutationOptions<
    certificateTypeCreateValidationType,
    unknown,
    certificateTypeCreateValidationType
  >
) => {
  return useMutation<
    certificateTypeCreateValidationType,
    unknown,
    certificateTypeCreateValidationType
  >({
    ...options,
    mutationFn: async (data: certificateTypeCreateValidationType) => {
      const res = await httpClient.post<certificateTypeCreateValidationType>(
        "/admin/certificate-types",
        { ...data }
      )
      return res.data
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
      queryClient.invalidateQueries(["certificate-types"])
      queryClient.invalidateQueries(["certificateTypesList"])
      notifications.show({
        title: "Added",
        message: "Successfully created certificate type",
        color: "green",
      })
    },
  })
}

export const useListStandardsQuery = () => {
  return useQuery(["standards-list"], async () => {
    const res = await httpClient.get<AdminResponses.StandardsList[]>(
      "/admin/standards-list"
    )
    return res.data
  })
}

export const useDeleteCertificateTypeMutation = (
  options?: UseMutationOptions<void, unknown, string>
) => {
  return useMutation({
    ...options,
    mutationFn: async (id: string) => {
      const res = await httpClient.delete(`/admin/certificate-types/${id}`)
      return res.data
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
      queryClient.invalidateQueries(["certificate-types"])
      queryClient.invalidateQueries(["certificateTypesList"])
      notifications.show({
        title: "Deleted",
        message: "Successfully deleted certificate type",
        color: "green",
      })
    },
  })
}
